import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { notify } from '@/lib/notify';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Pencil, UserCheck, Pause, Play, Building2, Users, Monitor, Calendar, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlatformHeader, MetricCard, StatusBadge, TierBadge } from '@/components/platform';
import { cn } from '@/lib/utils';
import { startImpersonationSession } from '@/lib/impersonation';

type TabValue = 'overview' | 'subscription' | 'users' | 'audit';
type SubscriptionTier = 'FREE' | 'STANDARD' | 'PREMIUM' | 'ENTERPRISE';
type SubscriptionStatus = 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'CANCELLED';

interface TenantDetailData {
  id: string;
  name: string;
  slug: string;
  billingEmail: string;
  logoUrl?: string;
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus;
  isTrial: boolean;
  trialEndsAt?: string;
  maxUsers: number;
  maxMachines: number;
  maxWarehouses: number;
  settings?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  userCount: number;
  machineCount: number;
  warehouseCount: number;
  recentActivity: Array<{
    action: string;
    actorEmail: string;
    createdAt: string;
  }>;
}

interface TenantUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface AuditLogEntry {
  id: string;
  action: string;
  actorId: string;
  actorType: string;
  targetType?: string;
  targetId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

const tabs: Array<{ value: TabValue; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'users', label: 'Users' },
  { value: 'audit', label: 'Audit Log' },
];

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [tenant, setTenant] = useState<TenantDetailData | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabValue>('overview');
  const [impersonateOpen, setImpersonateOpen] = useState(searchParams.get('impersonate') === 'true');
  const [impersonateReason, setImpersonateReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!id) return;

    async function loadTenant() {
      try {
        setLoading(true);
        const res = await apiFetch(`/api/v1/platform/tenants/${id}`);
        if (!res.ok) {
          if (res.status === 403) {
            setError('Access denied. Platform admin role required.');
            return;
          }
          if (res.status === 404) {
            setError('Tenant not found');
            return;
          }
          throw new Error('Failed to load tenant');
        }
        const data = await res.json();
        setTenant(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    loadTenant();
  }, [id]);

  const loadUsers = async () => {
    if (!id) return;
    try {
      const res = await apiFetch(`/api/v1/platform/tenants/${id}/users`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data.items || []);
      }
    } catch {
      // Ignore errors
    }
  };

  const loadAuditLogs = async () => {
    if (!id) return;
    try {
      const res = await apiFetch(`/api/v1/platform/tenants/${id}/audit-log`);
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.items || []);
      }
    } catch {
      // Ignore errors
    }
  };

  const handleTabChange = (tab: TabValue) => {
    setActiveTab(tab);
    if (tab === 'users') loadUsers();
    if (tab === 'audit') loadAuditLogs();
  };

  const handleSuspend = async () => {
    if (!tenant || !window.confirm(`Are you sure you want to suspend "${tenant.name}"?`)) return;
    try {
      const res = await apiFetch(`/api/v1/platform/tenants/${tenant.id}/suspend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Suspended via platform admin' }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTenant(prev => prev ? { ...prev, subscriptionStatus: updated.subscriptionStatus } : null);
      }
    } catch {
      notify.error('Failed to suspend tenant');
    }
  };

  const handleActivate = async () => {
    if (!tenant) return;
    try {
      const res = await apiFetch(`/api/v1/platform/tenants/${tenant.id}/activate`, {
        method: 'POST',
      });
      if (res.ok) {
        const updated = await res.json();
        setTenant(prev => prev ? { ...prev, subscriptionStatus: updated.subscriptionStatus, isTrial: updated.isTrial } : null);
      }
    } catch {
      notify.error('Failed to activate tenant');
    }
  };

  const handleImpersonate = async () => {
    if (!tenant || !impersonateReason.trim() || !selectedUserId) return;

    setImpersonating(true);
    try {
      const res = await apiFetch('/api/v1/platform/impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetOrganizationId: tenant.id,
          targetUserId: selectedUserId,
          reason: impersonateReason,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        notify.error(data.error || 'Failed to impersonate');
        return;
      }

      const result = await res.json();
      startImpersonationSession(result);
      window.location.href = '/';
    } catch {
      notify.error('Failed to impersonate');
    } finally {
      setImpersonating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PlatformHeader />
        <div className="flex items-center justify-center p-8">
          <p className="text-muted-foreground">Loading tenant details...</p>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PlatformHeader />
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <p className="text-red-600">{error || 'Tenant not found'}</p>
          <Button variant="outline" asChild>
            <Link to="/platform/tenants">Back to Tenants</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PlatformHeader />
      <div className="space-y-6">
        <div className="mb-6">
          <Link
            to="/platform/tenants"
            className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Tenants
          </Link>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{tenant.name}</h1>
                <StatusBadge status={tenant.subscriptionStatus} />
                <TierBadge tier={tenant.subscriptionTier} />
              </div>
              <p className="text-muted-foreground">/{tenant.slug}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to={`/platform/tenants/${tenant.id}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Link>
              </Button>
              <Button variant="outline" onClick={() => setImpersonateOpen(true)}>
                <UserCheck className="mr-2 h-4 w-4" />
                Impersonate
              </Button>
              {tenant.subscriptionStatus === 'SUSPENDED' ? (
                <Button onClick={handleActivate}>
                  <Play className="mr-2 h-4 w-4" />
                  Activate
                </Button>
              ) : (
                <Button variant="destructive" onClick={handleSuspend}>
                  <Pause className="mr-2 h-4 w-4" />
                  Suspend
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <MetricCard
            title="Users"
            value={`${tenant.userCount} / ${tenant.maxUsers}`}
            icon={<Users className="h-6 w-6" />}
          />
          <MetricCard
            title="Machines"
            value={`${tenant.machineCount} / ${tenant.maxMachines}`}
            icon={<Monitor className="h-6 w-6" />}
          />
          <MetricCard
            title="Warehouses"
            value={`${tenant.warehouseCount} / ${tenant.maxWarehouses}`}
            icon={<Building2 className="h-6 w-6" />}
          />
          <MetricCard
            title="Created"
            value={new Date(tenant.createdAt).toLocaleDateString()}
            icon={<Calendar className="h-6 w-6" />}
          />
        </div>

        <div className="space-y-6">
          <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => handleTabChange(tab.value)}
                className={cn(
                  'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
                  activeTab === tab.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'hover:bg-background/50'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Organization Info</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Name</dt>
                      <dd className="text-sm">{tenant.name}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Slug</dt>
                      <dd className="text-sm">{tenant.slug}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Billing Email</dt>
                      <dd className="text-sm">{tenant.billingEmail}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                      <dd className="text-sm">{new Date(tenant.createdAt).toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Last Updated</dt>
                      <dd className="text-sm">{new Date(tenant.updatedAt).toLocaleString()}</dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {tenant.recentActivity.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No recent activity</p>
                  ) : (
                    <div className="space-y-4">
                      {tenant.recentActivity.slice(0, 5).map((activity, index) => (
                        <div key={index} className="flex items-start justify-between text-sm">
                          <div>
                            <p className="font-medium">{activity.action}</p>
                            <p className="text-muted-foreground">{activity.actorEmail}</p>
                          </div>
                          <p className="text-muted-foreground">
                            {new Date(activity.createdAt).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'subscription' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Subscription Details</CardTitle>
                <CardDescription>Current subscription tier and limits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <h4 className="mb-4 font-medium">Current Plan</h4>
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Tier</dt>
                        <dd className="text-sm"><TierBadge tier={tenant.subscriptionTier} /></dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                        <dd className="text-sm"><StatusBadge status={tenant.subscriptionStatus} /></dd>
                      </div>
                      {tenant.isTrial && tenant.trialEndsAt && (
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Trial Ends</dt>
                          <dd className="text-sm">{new Date(tenant.trialEndsAt).toLocaleDateString()}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                  <div>
                    <h4 className="mb-4 font-medium">Limits</h4>
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Max Users</dt>
                        <dd className="text-sm">{tenant.maxUsers === 999999 ? 'Unlimited' : tenant.maxUsers}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Max Machines</dt>
                        <dd className="text-sm">{tenant.maxMachines === 999999 ? 'Unlimited' : tenant.maxMachines}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Max Warehouses</dt>
                        <dd className="text-sm">{tenant.maxWarehouses === 999999 ? 'Unlimited' : tenant.maxWarehouses}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'users' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tenant Users</CardTitle>
                <CardDescription>Users belonging to this organization</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>{`${user.firstName} ${user.lastName}`.trim() || '-'}</TableCell>
                        <TableCell>
                          <StatusBadge status={user.isActive ? 'ACTIVE' : 'SUSPENDED'} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setImpersonateOpen(true);
                            }}
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {activeTab === 'audit' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Audit Log</CardTitle>
                <CardDescription>Activity history for this tenant</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Action</TableHead>
                      <TableHead>Actor Type</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">{log.action}</TableCell>
                        <TableCell>{log.actorType}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {log.targetType ? `${log.targetType}: ${log.targetId?.slice(0, 8)}...` : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(log.createdAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {auditLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                          No audit logs found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={impersonateOpen} onOpenChange={setImpersonateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impersonate User</DialogTitle>
            <DialogDescription>
              Enter a reason for impersonating this user. This will be logged for audit purposes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="impersonate-user">User *</Label>
              <Select
                value={selectedUserId ?? ''}
                onValueChange={(value) => setSelectedUserId(value || undefined)}
              >
                <SelectTrigger id="impersonate-user">
                  <SelectValue placeholder="Select a user to view as" />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter((u) => u.isActive)
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email} ({u.email})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <Input
                id="reason"
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                placeholder="Support ticket #12345"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Session starts read-only. Use the banner to enable writes or stop impersonating.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImpersonate}
              disabled={!impersonateReason.trim() || !selectedUserId || impersonating}
            >
              {impersonating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Start Impersonation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
